import { MediaUploadType } from '@/components/features/Editor/utils';
import { type CollectionSchema } from '@/server/api/routers/collectionEditor';
import { type CollectionSectionType } from '@/types/collection';
import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { footerSection, getSections, headerSection, introSection } from './sections';
import { SavingState, ScreenType, type Section, type TemplateType } from './types';

type State = {
  saving: SavingState;
  showPreview: boolean;
  showPublish: boolean;
  chainId?: number;
  address?: string;
  slug?: string;
  initialFields?: CollectionSchema;
  template?: TemplateType;
  activeScreen: CollectionSectionType | ScreenType;
  addSectionIndex?: number;
  isAddingSection?: boolean;
  activeItem?: {
    section?: CollectionSectionType | ScreenType;
    item?: string;
  };
  nftGalleryTokenSources?: (string | undefined)[];
  disabledSections?: CollectionSectionType[];
  mediaUploadType?: MediaUploadType;
} & Pick<CollectionSchema, 'header' | 'intro' | 'sections' | 'footer'>;

type Methods = {
  setTemplate: (template: TemplateType) => void;
  setActiveScreen: (section: CollectionSectionType | ScreenType) => void;
  setSections: (
    newState: (sections: Section[] | undefined | null) => Section[] | undefined | null,
  ) => void;
  setSectionProps: (section: CollectionSectionType, props: Partial<Section>) => void;
  handleAddSection: (newSection: Section) => void;
  setSectionActiveItem: (section?: CollectionSectionType | ScreenType, item?: string) => void;
  resetState: () => void;
};

const initialState: State = {
  saving: SavingState.Idle,
  showPreview: false,
  showPublish: false,
  chainId: undefined,
  address: undefined,
  slug: undefined,
  initialFields: undefined,
  template: undefined,
  activeScreen: ScreenType.Overview,
  header: headerSection,
  intro: introSection,
  footer: footerSection,
  sections: [],
  addSectionIndex: undefined,
  isAddingSection: false,
  activeItem: undefined,
  nftGalleryTokenSources: [],
  disabledSections: [],
  mediaUploadType: undefined,
};

type CollectionEditorStore = State & Methods;

export const useEditor = createWithEqualityFn(
  immer<CollectionEditorStore>((set, get) => ({
    ...initialState,
    setTemplate: template =>
      set({ template, sections: getSections(template, get().disabledSections) }),
    setActiveScreen: section => set({ activeScreen: section }),
    setSections: newState => {
      set(state => ({ sections: newState(state.sections) }));
    },
    setSectionProps: (CollectionsectionType, props) =>
      set({
        sections: get().sections?.map(section =>
          section.type === CollectionsectionType ? { ...section, ...props } : section,
        ),
      }),
    setSectionActiveItem: (section, item) =>
      set({
        activeItem: section ? { section, item } : undefined,
      }),
    handleAddSection: newSection => {
      const { sections, addSectionIndex } = get();

      const updatedSections = [
        ...sections.slice(
          0,
          typeof addSectionIndex === 'number' ? addSectionIndex : sections.length,
        ),
        newSection,
        ...sections.slice(typeof addSectionIndex === 'number' ? addSectionIndex : sections.length),
      ];
      set({ sections: updatedSections, isAddingSection: false });
    },
    resetState: () => set(initialState),
  })),
  shallow,
);
